import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getSignupOpen } from "@/lib/admin.functions";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Admin Sign In — Tacoya" }, { name: "robots", content: "noindex" }] }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const signupOpenFn = useServerFn(getSignupOpen);
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [signupOpen, setSignupOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    void import("@/integrations/supabase/client").then(({ supabase }) =>
      supabase.auth.getUser().then(({ data }) => {
        if (data.user) navigate({ to: "/admin" });
      }),
    );
  }, [navigate]);

  useEffect(() => {
    signupOpenFn().then((r) => {
      setSignupOpen(r.open);
      setMode(r.open ? "signup" : "signin");
    }).catch(() => setSignupOpen(false));
  }, [signupOpenFn]);


  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/admin" },
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
      navigate({ to: "/admin" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-charcoal px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display text-5xl text-yellow tracking-wide">TACOYA</Link>
        <p className="mt-2 text-center text-xs uppercase tracking-[0.3em] text-cream/50">Content Admin</p>

        {signupOpen && (
          <div className="mt-6 rounded-2xl border border-yellow/30 bg-yellow/10 px-4 py-3 text-center text-xs text-yellow">
            No admin exists yet — create the first admin account below.
          </div>
        )}

        <form onSubmit={submit} className="mt-8 rounded-3xl border border-cream/10 bg-card p-6 sm:p-8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.9)]">
          <h1 className="font-display text-3xl text-cream uppercase tracking-tight">
            {mode === "signin" ? "Sign In" : signupOpen ? "Create First Admin" : "Create Account"}
          </h1>
          <p className="mt-1 text-sm text-cream/60">
            {mode === "signin" ? "Access the content management panel." : "This first account becomes the admin & super admin."}
          </p>

          <label className="mt-6 block">
            <span className="text-[11px] uppercase tracking-widest text-cream/60">Email</span>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-cream/15 bg-charcoal/60 px-4 py-3 text-cream outline-none focus:border-yellow"
              placeholder="you@tacoya.fi"
            />
          </label>
          <label className="mt-4 block">
            <span className="text-[11px] uppercase tracking-widest text-cream/60">Password</span>
            <input
              type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 w-full rounded-xl border border-cream/15 bg-charcoal/60 px-4 py-3 text-cream outline-none focus:border-yellow"
              placeholder="••••••••"
            />
          </label>

          {error && <p className="mt-4 text-sm text-chili">{error}</p>}

          <button
            type="submit" disabled={loading}
            className="mt-6 w-full rounded-full bg-yellow text-charcoal px-6 py-3.5 text-sm uppercase tracking-widest font-bold hover:bg-cream transition-colors disabled:opacity-60"
          >
            {loading ? "Please wait…" : mode === "signin" ? "Sign In" : "Sign Up"}
          </button>

          {signupOpen && (
            <button
              type="button"
              onClick={() => { setMode(mode === "signin" ? "signup" : "signin"); setError(null); }}
              className="mt-4 w-full text-center text-xs text-cream/60 hover:text-yellow"
            >
              {mode === "signin" ? "First time? Create the admin account" : "Already have an account? Sign in"}
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
